import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Plus, LogOut, LayoutGrid, Wallet, Trash2, Upload, Link2, X, Loader2, Inbox, Mail, Phone, Circle, Pencil } from "lucide-react";

const COUNTRIES = ["Turkey", "Northern Cyprus", "Germany", "France", "Spain", "Italy", "Netherlands", "Portugal", "Austria", "Greece", "Belgium", "Sweden", "Poland", "Czech Republic", "Denmark", "Ireland"];
const TYPES = ["apartment", "villa", "house", "commercial", "land"];
const PURPOSES = ["living", "investment", "business"];
const CURRENCIES = [
  { code: "EUR", symbol: "€" },
  { code: "USD", symbol: "$" },
  { code: "GBP", symbol: "£" },
  { code: "TRY", symbol: "₺" },
];

function fmtPrice(price, currency) {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 0,
      currencyDisplay: "narrowSymbol",
    }).format(price);
  } catch {
    return `${currency} ${Number(price).toLocaleString()}`;
  }
}

const EMPTY = {
  title: "", description: "", country: "Germany", city: "", price: 0,
  currency: "EUR", bedrooms: 1, bathrooms: 1, area_sqm: 0,
  property_type: "apartment", purpose_tags: ["living"], images: [], source_url: null,
};

export default function AgentDashboard() {
  const [lang] = useState(() => localStorage.getItem("lang") || "en");
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("listings"); // listings | leads
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("manual"); // manual | import
  const [editingId, setEditingId] = useState(null); // null = create; string = editing that property
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const [list, ldata] = await Promise.all([
        api.get("/properties/mine"),
        api.get("/leads/mine"),
      ]);
      setProperties(list.data);
      setLeads(ldata.data);
    } catch {
      navigate("/agent/login");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("token"); localStorage.removeItem("user");
    navigate("/");
  };

  const openForm = (m) => { setMode(m); setEditingId(null); setForm(EMPTY); setImportUrl(""); setShowForm(true); };

  const openEdit = (p) => {
    setEditingId(p.property_id);
    setMode("manual");
    setForm({
      title: p.title || "",
      description: p.description || "",
      country: p.country || "Germany",
      city: p.city || "",
      price: p.price || 0,
      currency: p.currency || "EUR",
      bedrooms: p.bedrooms || 0,
      bathrooms: p.bathrooms || 0,
      area_sqm: p.area_sqm || 0,
      property_type: p.property_type || "apartment",
      purpose_tags: p.purpose_tags?.length ? p.purpose_tags : ["living"],
      images: p.images || [],
      source_url: p.source_url || null,
    });
    setImportUrl("");
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const runImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/properties/import", { url: importUrl.trim() });
      setForm({ ...EMPTY, ...data, purpose_tags: data.purpose_tags?.length ? data.purpose_tags : ["living"] });
      toast.success("Imported! Review and publish");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import failed";
      toast.error(msg, { duration: 8000 });
    } finally {
      setImporting(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.data_url] }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, images: form.images.filter(Boolean) };
      if (editingId) {
        const { data } = await api.put(`/properties/${editingId}`, payload);
        setProperties((p) => p.map((x) => x.property_id === editingId ? data : x));
        toast.success("Listing updated");
      } else {
        const { data } = await api.post("/properties", payload);
        setProperties((p) => [data, ...p]);
        toast.success("Listing published");
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete listing?")) return;
    await api.delete(`/properties/${id}`);
    setProperties((p) => p.filter((x) => x.property_id !== id));
  };

  const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <aside className="fixed inset-y-0 left-0 w-64 hidden md:flex flex-col hairline border-l-0 border-t-0 border-b-0 p-6 bg-white">
        <Link to="/" className="font-display text-lg font-medium tracking-tight flex items-center gap-2 mb-10">
          <span className="w-6 h-6 grid place-items-center bg-black text-white font-black">Æ</span>
          Aegis Realtor
        </Link>
        <nav className="flex-1 flex flex-col gap-1 text-sm">
          <button data-testid="nav-listings" onClick={() => setTab("listings")} className={`flex items-center gap-3 px-3 py-2 rounded-md text-left ${tab === "listings" ? "bg-black text-white" : "hover:bg-[var(--paper-2)]"}`}>
            <LayoutGrid className="w-4 h-4" strokeWidth={1.75} /> Listings
          </button>
          <button data-testid="nav-leads" onClick={() => setTab("leads")} className={`flex items-center gap-3 px-3 py-2 rounded-md text-left justify-between ${tab === "leads" ? "bg-black text-white" : "hover:bg-[var(--paper-2)]"}`}>
            <span className="flex items-center gap-3"><Inbox className="w-4 h-4" strokeWidth={1.75} /> Leads</span>
            {leads.filter((l) => l.status === "new").length > 0 && (
              <span data-testid="leads-badge" className={`text-[10px] font-bold px-1.5 rounded-full ${tab === "leads" ? "bg-white text-black" : "bg-black text-white"}`}>
                {leads.filter((l) => l.status === "new").length}
              </span>
            )}
          </button>
          <Link data-testid="nav-billing" to="/agent/billing" className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[var(--paper-2)]">
            <Wallet className="w-4 h-4" strokeWidth={1.75} /> {t(lang, "billing")}
          </Link>
        </nav>
        <div className="text-xs text-muted-foreground">
          <div className="mb-2">{user?.email}</div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 hover:text-black">
            <LogOut className="w-3.5 h-3.5" /> {t(lang, "signOut")}
          </button>
        </div>
      </aside>

      <main className="md:pl-64 p-6 sm:p-10">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              {t(lang, "trial")} · {daysLeft} {t(lang, "daysLeft")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
              {tab === "listings" ? t(lang, "myListings") : "Buyer Leads"}
            </h1>
          </div>
          {tab === "listings" && !showForm && (
            <div className="flex gap-2">
              <button
                data-testid="add-listing-btn"
                onClick={() => openForm("manual")}
                className="flex items-center gap-2 bg-black text-white px-5 py-3 hover:opacity-90"
              >
                <Plus className="w-4 h-4" /> Manual add
              </button>
              <button
                data-testid="import-listing-btn"
                onClick={() => openForm("import")}
                className="flex items-center gap-2 bg-white hairline px-5 py-3 hover:bg-[var(--paper-2)]"
              >
                <Link2 className="w-4 h-4" /> Import from URL
              </button>
            </div>
          )}
        </div>

        {tab === "leads" ? (
          <LeadsPanel leads={leads} onMark={async (id) => {
            await api.post(`/leads/${id}/mark`, { status: "read" });
            setLeads((prev) => prev.map((l) => l.lead_id === id ? { ...l, status: "read" } : l));
          }} />
        ) : (<></>)}
        {tab === "listings" && showForm && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="hairline bg-white p-8 mb-10">
            <div className="flex items-center justify-between mb-6">
              {editingId ? (
                <div className="flex items-center gap-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Editing</div>
                  <div className="font-display text-lg font-medium truncate max-w-[400px]">{form.title || "Listing"}</div>
                </div>
              ) : (
                <div className="flex gap-1 hairline p-1">
                  <button
                    data-testid="mode-manual"
                    onClick={() => setMode("manual")}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold ${mode === "manual" ? "bg-black text-white" : ""}`}
                  >
                    Manual
                  </button>
                  <button
                    data-testid="mode-import"
                    onClick={() => setMode("import")}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold ${mode === "import" ? "bg-black text-white" : ""}`}
                  >
                    Import URL
                  </button>
                </div>
              )}
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-muted-foreground hover:text-black">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!editingId && mode === "import" && (
              <div className="mb-8">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Listing URL from another site</label>
                <div className="flex gap-2 mt-2">
                  <input
                    data-testid="import-url"
                    type="url"
                    placeholder="https://www.rightmove.co.uk/..."
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    className="flex-1 px-3 py-2 hairline focus:border-black outline-none"
                  />
                  <button
                    data-testid="import-fetch-btn"
                    type="button"
                    onClick={runImport}
                    disabled={importing || !importUrl.trim()}
                    className="px-5 py-2 bg-black text-white disabled:opacity-40 flex items-center gap-2"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Fetch
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Aegis will scrape the page, use AI to extract fields, and preload the form below. Review before publishing.</p>
              </div>
            )}

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                ["title", "Title"], ["city", "City"],
                ["bedrooms", "Bedrooms", "number"], ["bathrooms", "Bathrooms", "number"], ["area_sqm", "Area (m²)", "number"],
              ].map(([k, l, type]) => (
                <div key={k}>
                  <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{l}</label>
                  <input
                    data-testid={`prop-${k}`}
                    required={k === "title" || k === "city"}
                    type={type || "text"}
                    value={form[k]}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value }))}
                    className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none"
                  />
                </div>
              ))}
              <div className="md:col-span-1">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Price</label>
                <div className="flex mt-2">
                  <input
                    data-testid="prop-price"
                    required
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: Number(e.target.value) }))}
                    className="flex-1 px-3 py-2 hairline border-r-0 focus:border-black outline-none"
                  />
                  <select
                    data-testid="prop-currency"
                    value={form.currency}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                    className="px-3 py-2 hairline bg-white focus:border-black outline-none font-mono text-sm"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Country</label>
                <select
                  data-testid="prop-country"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  className="w-full mt-2 px-3 py-2 hairline bg-white focus:border-black outline-none"
                >
                  {COUNTRIES.includes(form.country) ? null : <option>{form.country}</option>}
                  {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Type</label>
                <select
                  data-testid="prop-type"
                  value={form.property_type}
                  onChange={(e) => setForm((f) => ({ ...f, property_type: e.target.value }))}
                  className="w-full mt-2 px-3 py-2 hairline bg-white focus:border-black outline-none"
                >
                  {TYPES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Purpose</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {PURPOSES.map((p) => {
                    const active = form.purpose_tags.includes(p);
                    return (
                      <button
                        type="button" key={p}
                        data-testid={`purpose-${p}`}
                        onClick={() => setForm((f) => ({ ...f, purpose_tags: active ? f.purpose_tags.filter((x) => x !== p) : [...f.purpose_tags, p] }))}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] hairline ${active ? "bg-black text-white" : ""}`}
                      >{p}</button>
                    );
                  })}
                </div>
              </div>

              {/* Image upload + gallery */}
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Images</label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {(form.images || []).map((src, i) => (
                    <div key={i} className="relative w-24 h-24 hairline overflow-hidden bg-[var(--paper-2)]">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-image-${i}`}
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-black text-white"
                      ><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label
                    data-testid="upload-image-label"
                    className="w-24 h-24 hairline grid place-items-center cursor-pointer hover:bg-[var(--paper-2)] text-xs text-muted-foreground"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mb-1" strokeWidth={1.5} /><span className="text-[9px] uppercase tracking-[0.15em]">Upload</span></>}
                    <input
                      data-testid="upload-image-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadFile(e.target.files?.[0])}
                    />
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">JPG/PNG/WEBP up to 5MB each. Add multiple.</p>
              </div>

              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Description</label>
                <textarea
                  data-testid="prop-description"
                  required rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none"
                />
              </div>

              <div className="md:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-5 py-2 hairline">Cancel</button>
                <button data-testid="prop-submit" type="submit" className="px-5 py-2 bg-black text-white">{editingId ? "Save changes" : "Publish"}</button>
              </div>
            </form>
          </motion.div>
        )}

        {tab === "listings" && (
          properties.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground text-sm">No listings yet. Click &ldquo;Manual add&rdquo; or &ldquo;Import from URL&rdquo;.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((p) => (
                <div key={p.property_id} data-testid={`mine-${p.property_id}`} className="hairline bg-white p-4 flex gap-4">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-24 h-24 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-medium truncate">{p.title}</h3>
                    <div className="text-xs text-muted-foreground">{p.city}, {p.country}</div>
                    <div className="mt-2 font-mono text-sm">{fmtPrice(p.price, p.currency)}</div>
                    {p.source_url && <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">imported</div>}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <button data-testid={`edit-${p.property_id}`} onClick={() => openEdit(p)} className="text-muted-foreground hover:text-black" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-${p.property_id}`} onClick={() => remove(p.property_id)} className="text-muted-foreground hover:text-black" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}

function LeadsPanel({ leads, onMark }) {
  if (leads.length === 0) {
    return <div className="text-center py-24 text-muted-foreground text-sm">No leads yet. When buyers request contact on your listings, they will appear here.</div>;
  }
  return (
    <div className="flex flex-col gap-3" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.lead_id} data-testid={`lead-${l.lead_id}`} className="hairline bg-white p-5 flex flex-col md:flex-row gap-4 md:items-center">
          {l.property_image && <img src={l.property_image} alt="" className="w-full md:w-24 h-24 object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {l.status === "new" && <Circle className="w-2 h-2 fill-black text-black" data-testid={`lead-badge-${l.lead_id}`} />}
              <div className="font-display text-lg font-medium truncate">{l.buyer_name}</div>
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
              Interested in · {l.property_title} · {l.property_city}, {l.property_country}
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <a href={`mailto:${l.buyer_email}`} className="flex items-center gap-1.5 hover:underline underline-offset-4"><Mail className="w-3.5 h-3.5" /> {l.buyer_email}</a>
              <a href={`tel:${l.buyer_phone}`} className="flex items-center gap-1.5 hover:underline underline-offset-4"><Phone className="w-3.5 h-3.5" /> {l.buyer_phone}</a>
            </div>
            {l.message && <p className="mt-3 text-sm text-muted-foreground italic">&ldquo;{l.message}&rdquo;</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-mono">
              {new Date(l.created_at).toLocaleDateString()}
            </div>
            {l.status === "new" && (
              <button
                data-testid={`mark-read-${l.lead_id}`}
                onClick={() => onMark(l.lead_id)}
                className="text-[10px] tracking-[0.2em] uppercase font-bold hairline px-3 py-1.5 hover:bg-[var(--paper-2)]"
              >
                Mark read
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
